import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { TRANSLATIONS, LANGUAGES } from "@/i18n/translations";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LanguageContext = createContext(null);
const SUPPORTED = LANGUAGES.map((l) => l.code);
const LOCALES = { en: "en-GB", nl: "nl-NL", fr: "fr-FR", de: "de-DE", es: "es-ES", it: "it-IT", pt: "pt-PT" };

function detectInitial() {
  const stored = localStorage.getItem("decide_lang");
  if (stored && SUPPORTED.includes(stored)) return stored;
  // Main language is English by default; users can switch anytime in Settings.
  return "en";
}

export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(detectInitial);

  // adopt the user's saved language when it loads; otherwise persist local pref to their account
  useEffect(() => {
    if (!user) return;
    if (user.language && SUPPORTED.includes(user.language)) {
      if (user.language !== lang) {
        setLangState(user.language);
        localStorage.setItem("decide_lang", user.language);
      }
    } else {
      api.patch("/auth/language", { language: lang }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user]);

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;
    setLangState(code);
    localStorage.setItem("decide_lang", code);
    document.documentElement.lang = code;
    api.patch("/auth/language", { language: code }).catch(() => {});
  }, []);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const t = useCallback((key, vars) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    let str = dict[key] ?? TRANSLATIONS.en[key] ?? key;
    if (vars) {
      Object.keys(vars).forEach((k) => { str = str.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]); });
    }
    return str;
  }, [lang]);

  const locale = LOCALES[lang] || "en-GB";

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, locale, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
